import { useState } from 'react';
import { T, useGT } from 'gt-react';
import {
  NOTIFICATION_ACTION_PAYLOAD_KEY,
  type NotificationActionPayload,
} from '@erp/shared-types';
import { Button } from 'src/components/ui/button';
import { rpcClient } from 'src/api/rpc/client';
import { useToast } from 'src/context/ToastContext';

type RpcCallable = (input: unknown) => Promise<unknown>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isActionPayload(value: unknown): value is NotificationActionPayload {
  if (!isRecord(value)) return false;
  return (
    typeof value['name'] === 'string' &&
    typeof value['endpoint'] === 'string' &&
    typeof value['actionPayload'] === 'string' &&
    (value['buttonType'] === 'primary' || value['buttonType'] === 'secondary')
  );
}

export function extractActions(
  payload: Record<string, unknown>,
): NotificationActionPayload[] {
  const raw = payload[NOTIFICATION_ACTION_PAYLOAD_KEY];
  if (!Array.isArray(raw)) return [];
  return raw.filter(isActionPayload);
}

function resolveRpcCallable(endpoint: string): RpcCallable | null {
  const parts = endpoint.split('.');
  let cursor: unknown = rpcClient as unknown;
  for (const part of parts) {
    if (!isRecord(cursor)) return null;
    cursor = cursor[part];
  }
  return typeof cursor === 'function' ? (cursor as RpcCallable) : null;
}

const INVALID_PAYLOAD = Symbol('invalid-payload');

function parseActionPayload(raw: string): unknown | typeof INVALID_PAYLOAD {
  try {
    return JSON.parse(raw);
  } catch {
    return INVALID_PAYLOAD;
  }
}

interface Props {
  actions: NotificationActionPayload[];
  className?: string;
}

export function NotificationActionButtons({ actions, className }: Props) {
  const gt = useGT();
  const { showToast } = useToast();
  const [pendingIndex, setPendingIndex] = useState<number | null>(null);

  if (actions.length === 0) return null;

  const dispatch = async (action: NotificationActionPayload, index: number) => {
    const callable = resolveRpcCallable(action.endpoint);
    if (!callable) {
      showToast({
        title: gt('Action unavailable'),
        message: gt('No handler registered for this action.'),
        color: 'error',
      });
      return;
    }
    const input = parseActionPayload(action.actionPayload);
    if (input === INVALID_PAYLOAD) {
      showToast({
        title: gt('Action failed'),
        message: gt('Invalid action payload.'),
        color: 'error',
      });
      return;
    }
    setPendingIndex(index);
    try {
      await callable(input);
      showToast({
        title: action.name,
        message: gt('Action completed.'),
        color: 'success',
      });
    } catch (err) {
      showToast({
        title: gt('Action failed'),
        message: err instanceof Error ? err.message : String(err),
        color: 'error',
      });
    } finally {
      setPendingIndex(null);
    }
  };

  return (
    <div className={className ?? 'mt-2.5 flex flex-wrap gap-2'}>
      {actions.map((action, index) => (
        <Button
          key={`${action.endpoint}:${action.name}:${index}`}
          variant={action.buttonType === 'primary' ? 'default' : 'outline'}
          size="sm"
          disabled={pendingIndex !== null}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void dispatch(action, index);
          }}
        >
          {pendingIndex === index ? <T>Working…</T> : action.name}
        </Button>
      ))}
    </div>
  );
}
